/**
 * The part of Web Audio the page's sound uses, as interfaces a browser's `AudioContext` satisfies and a test stubs
 * (ADR-085 decision 8).
 */

/**
 * A node that connects onward.
 */
export interface NodeLike {
  connect(destination: NodeLike): unknown;
}

/**
 * A parameter of a node, a gain's level or a panner's pan: set at a time, or eased towards a target from a time on.
 */
export interface ParamLike {
  value: number;
  setValueAtTime(value: number, startTime: number): unknown;
  setTargetAtTime(target: number, startTime: number, timeConstant: number): unknown;
  cancelScheduledValues(startTime: number): unknown;
}

/**
 * A gain node.
 */
export interface GainLike extends NodeLike {
  readonly gain: ParamLike;
}

/**
 * A stereo panner.
 */
export interface PannerLike extends NodeLike {
  readonly pan: ParamLike;
}

/**
 * A decoded buffer, opaque to the page.
 */
export type BufferLike = object;

/**
 * A one-shot buffer player.
 */
export interface SourceLike extends NodeLike {
  buffer: BufferLike | null;
  onended: (() => void) | null;
  start(): void;
  stop(when?: number): void;
}

/**
 * A media element the music streams from.
 */
export interface MediaLike {
  src: string;
  crossOrigin: string | null;
  preload: string;
  readonly currentTime: number;
  readonly duration: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: "timeupdate" | "ended" | "error", listener: () => void): void;
}

/**
 * The audio context.
 */
export interface ContextLike {
  readonly currentTime: number;
  readonly state: string;
  readonly destination: NodeLike;
  createGain(): GainLike;
  createStereoPanner(): PannerLike;
  createBufferSource(): SourceLike;
  createMediaElementSource(element: MediaLike): NodeLike;
  decodeAudioData(data: ArrayBuffer): Promise<BufferLike>;
  resume(): Promise<void>;
  suspend(): Promise<void>;
}

/**
 * The browser's audio context, or null where the browser has none.
 *
 * @returns A new context.
 */
export function browserContext(): ContextLike | null {
  const Constructor = typeof window === "undefined" ? undefined : window.AudioContext;
  return Constructor === undefined ? null : (new Constructor() as unknown as ContextLike);
}

/**
 * A new media element for music, or null where the page has no document.
 *
 * @returns The element.
 */
export function browserMedia(): MediaLike | null {
  return typeof document === "undefined" ? null : (document.createElement("audio") as unknown as MediaLike);
}

/**
 * Whether the browser can play a media type, as `canPlayType` answers.
 *
 * @param type The media type.
 * @returns True for "maybe" or "probably".
 */
export function browserCanPlay(type: string): boolean {
  return typeof document === "undefined" ? false : document.createElement("audio").canPlayType(type) !== "";
}
